import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  documentPaperFor,
  type DocumentPaper,
  type DocumentPaperKind,
  type DocumentPapers,
} from '@goyone/shared';
import { api } from '../lib/api';

/**
 * ورقةُ المستند المطبوع (ليتر هيد A4) — إن رُفعت في «الهوية والمستندات».
 *
 * `ready` تنتظره صفحةُ الطباعة قبل أن ترسم: وإلّا ظهرت ترويسةُ النظام لحظةً
 * ثمّ اختفت تحت الورقة، أو طُبع المستند قبل وصولها. والخطأُ في جلبها لا يوقف
 * الطباعة — يُطبع المستند بترويسة النظام كما كان.
 */
export function useDocumentPaper(kind: DocumentPaperKind): { paper: DocumentPaper | null; ready: boolean } {
  const papers = useQuery({
    queryKey: ['settings', 'papers'],
    queryFn: () => api<DocumentPapers>('/settings/papers'),
    staleTime: 5 * 60_000,
  });
  return { paper: documentPaperFor(papers.data, kind), ready: !papers.isPending };
}

/**
 * صنفُ الورقة وأسلوبُها على `.print-page`.
 *
 * بلا ورقة: لا شيء، فيبقى المستند كما كان. وبها: `has-paper` يُخفي ترويسةَ
 * النظام وتذييلَه وبياناتِ البنك (تصميمُ الورقة يحملها)، والهوامشُ متغيّراتٌ
 * تقرؤها print.css — على الشاشة حشوةُ الورقة، وفي الطباعة حشوةُ كلِّ صفحة.
 */
export function paperPageProps(
  paper: DocumentPaper | null,
  className: string,
  style?: CSSProperties,
): { className: string; style?: CSSProperties } {
  if (!paper) return { className, style };
  return {
    className: `${className} has-paper`,
    style: {
      ...style,
      ['--paper-top' as string]: `${paper.top}mm`,
      ['--paper-bottom' as string]: `${paper.bottom}mm`,
      ['--paper-side' as string]: `${paper.side}mm`,
      backgroundImage: `url("${paper.imageUrl}")`,
    },
  };
}

/**
 * الورقةُ خلفيّةً لكلّ صفحةٍ مطبوعة.
 *
 * عنصرٌ ثابتُ الموضع (`position: fixed`) يكرّره المتصفّح في كلّ صفحةٍ عند
 * الطباعة، بحجم A4 كاملًا من حافّة الورق (`@page paper { margin: 0 }`).
 * على الشاشة مخفيّ: الورقةُ هناك خلفيّةُ `.print-page` نفسها.
 */
export function PaperBackground({ paper }: { paper: DocumentPaper | null }) {
  if (!paper) return null;
  return <img src={paper.imageUrl} alt="" className="paper-bg" aria-hidden />;
}
